import React from "react";
import Icon from "../Icon";
import styles from "./AddressSearch.module.scss";
import type { GroupSuggestion, DetailSuggestion, AddressSearchStrings } from "./index";
import Typography from "../../components/Typography";

type Props = {
  level: "groups" | "details";
  groups: GroupSuggestion[];
  details: DetailSuggestion[];
  highlight: number;
  onHover: (i: number) => void;
  onSelectGroup: (group: GroupSuggestion) => void;
  onSelectDetail: (detail: DetailSuggestion) => void;
  onEnterManual?: () => void;
  query:string;
  strings:AddressSearchStrings;
};

type SplitResult = { prefix: string; rest: string };

function splitMatchFromStart(displayCode: string, userQuery: string): SplitResult {
  const normalizedQuery = (userQuery ?? "").replace(/\s+/g, "").toLowerCase();
  if (!normalizedQuery) return { prefix: "", rest: displayCode };
  let queryIdx = 0, codeIdx = 0, prefixEndIdx = 0;
  while (codeIdx < displayCode.length && queryIdx < normalizedQuery.length) {
    const ch = displayCode[codeIdx];
    if (/\s/.test(ch)) { codeIdx++; prefixEndIdx = codeIdx; continue; }
    if (ch.toLowerCase() !== normalizedQuery[queryIdx]) return { prefix: "", rest: displayCode };
    queryIdx++; codeIdx++; prefixEndIdx = codeIdx;
  }
  if (queryIdx < normalizedQuery.length) return { prefix: "", rest: displayCode };
  return { prefix: displayCode.slice(0, prefixEndIdx), rest: displayCode.slice(prefixEndIdx) };
}

/**
 * ResultsPanel
 *
 * Pure presentational listbox for groups or details with a footer for manual entry.
 * Does not own state; all selection/highlight is driven via props.
 *
 * @param level Which list to show: "groups" or "details".
 * @param groups Group suggestions to render (when level === "groups").
 * @param details Detail suggestions to render (when level === "details").
 * @param highlight Index of the currently highlighted row, or -1.
 * @param onHover Called with row index when a row is hovered.
 * @param onSelectGroup Called with the clicked group.
 * @param onSelectDetail Called with the clicked detail.
 * @param onEnterManual Called when either footer button is clicked.
 * @param query Raw user query (used for prefix highlight).
 */
export function ResultsPanel({
  level, groups, details, highlight, onHover,
  onSelectGroup, onSelectDetail, onEnterManual, query, strings
}: Props) {
return (
    <div data-testid="results-panel" className={styles.panel}>
      <ul className={styles.list} role="listbox" aria-label={strings.listAriaLabel}>
        {level === "groups" &&
          groups.map((group, i) => {
            const { prefix, rest } = splitMatchFromStart(group.code, query);
            return (
              <li
                key={group.id}
                role="option"
                aria-selected={i === highlight}
                className={`${styles.item} ${i === highlight ? styles.itemActive : ""}`}
                onMouseEnter={() => onHover(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelectGroup(group)}
              >
                <div className={styles.itemLeft}>
                  <Icon name="mapPin" size="sm" />
                  <div className={styles.inlineText}>
                    <span className={styles.itemCode}>
                      {prefix && (
                        <Typography component="span" variant="text" size="md" weight="semibold" className={styles.codePrefix}>
                          {prefix}
                        </Typography>
                      )}
                      {rest && (
                        <Typography component="span" variant="text" size="md" className={styles.codeRest}>
                          {rest}
                        </Typography>
                      )}
                    </span>
                    <span className={styles.itemSep} aria-hidden> </span>
                    <Typography component="span" variant="text" size="md" className={styles.itemLabel}>
                      {group.label}
                    </Typography>
                  </div>
                </div>
                <div className={styles.itemRight}>
                  <Typography component="span" variant="text" size="md" className={styles.itemCount}>
                    {group.count} {strings.addressesSuffix}
                  </Typography>
                  <Icon name="chevronRight" size="sm" />
                </div>
              </li>
            );
          })}

        {level === "details" &&
          details.map((detail, i) => (
            <li
              key={detail.id}
              role="option"
              aria-selected={i === highlight}
              className={`${styles.item} ${i === highlight ? styles.itemActive : ""}`}
              onMouseEnter={() => onHover(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelectDetail(detail)}
            >
              <div className={styles.itemLeft}>
                <Icon name="mapPin" size="sm" />
                <div className={styles.itemText}>
                  <Typography component="span" variant="text" size="sm" className={styles.itemLabel}>
                    {detail.label}
                  </Typography>
                </div>
              </div>
            </li>
          ))}
      </ul>

  {onEnterManual ? (
      <div className={styles.footer}>
        <button
          type="button"
          className={styles.footerBtnLeft}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onEnterManual}
        >
          <Typography component="span" variant="text" size="md" weight="semibold">
            {strings.notListed}
          </Typography>
        </button>
        <button
          type="button"
          className={styles.footerBtnRight}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onEnterManual}
        >
          <Typography component="span" variant="text" size="md" weight="medium">
            {strings.enterManually}
          </Typography>
        </button>
      </div>
  ) : null}
    </div>
  );
}